import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookMarked,
  Sparkles,
  Terminal,
  UploadCloud,
  Users,
} from 'lucide-react';
import type { CSSProperties, ComponentType, ReactNode } from 'react';

/**
 * Public marketing landing page served at `/` for logged-out visitors
 * (COR-863 / COR-861). Unlike the authenticated dashboard it does NOT follow
 * the in-app light/dark theme toggle — it pins its own fixed CoreFi brand
 * palette (light surface, navy ink, teal accent) so the colour logo and the
 * marketing look stay consistent for anonymous visitors regardless of any
 * theme preference a returning user may have persisted.
 *
 * Copy is the final positioning from the CMO (sibling issue COR-864, the
 * `landing-copy` document). CTA labels come from that doc; the primary CTA
 * routes into the OIDC sign-in flow.
 */

const BRAND = {
  ink: '#0e1622',
  deep: '#071421',
  slate: '#5c6473',
  teal: '#1ebdae',
  tealDark: '#159b8f',
  line: '#e5e8ee',
  wash: '#f7f8fa',
} as const;

/**
 * CoreFi lead-capture pop-up embed (COR-869). The `data-evenfi-popup` button
 * is wired up by `forms.corefi.co/embed.js`, which opens the shared
 * `fluentix-lead` form as a dimmed modal on click — replacing the old
 * `mailto:` CTAs so anonymous visitors can request access without an email
 * client.
 */
const LEAD_EMBED_SRC = 'https://forms.corefi.co/embed.js';

/** Injects the lead-capture embed script once, only while the landing page is mounted. */
function useLeadFormEmbed() {
  useEffect(() => {
    if (document.querySelector(`script[src="${LEAD_EMBED_SRC}"]`)) return;
    const script = document.createElement('script');
    script.src = LEAD_EMBED_SRC;
    script.async = true;
    document.body.appendChild(script);
  }, []);
}

interface LeadButtonProps {
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

/** Button that opens the CoreFi `fluentix-lead` pop-up form (see {@link useLeadFormEmbed}). */
function LeadButton({ className, style, children }: LeadButtonProps) {
  return (
    <button
      type="button"
      data-evenfi-popup="fluentix-lead"
      data-popup-layout="modal"
      data-popup-overlay="dim"
      className={className}
      style={style}
    >
      {children}
    </button>
  );
}

interface Feature {
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  title: string;
  body: string;
}

const FEATURES: Feature[] = [
  {
    icon: Sparkles,
    title: 'AI translation you can trust',
    body: 'Glossary-aware, memory-backed AI translation across Anthropic and OpenAI. Fluentix reuses your approved terminology and past translations so every string sounds like your brand — not a machine.',
  },
  {
    icon: BookMarked,
    title: 'Glossary + translation memory',
    body: 'Lock in terminology once and never re-translate the same string twice. Your glossary and translation memory travel across every project and language, keeping tone and terms consistent everywhere.',
  },
  {
    icon: Terminal,
    title: 'Built for developers',
    body: 'A first-class CLI, ready-made CI jobs, and PO/JSON import-export keep translations next to your code. Push source strings from a pipeline, pull finished translations back — no manual copy-paste.',
  },
  {
    icon: UploadCloud,
    title: 'Publish on autopilot',
    body: 'S3 delivery and CI triggers push reviewed translations straight to your apps. New locale files land exactly where your runtime loads them, the moment they’re approved.',
  },
  {
    icon: Users,
    title: 'Workspaces built for teams',
    body: 'Organize products, partners, and languages under one roof. CoreFi products and partners like Borseria and core-admin manage localization side by side, with role-based access and a full audit trail.',
  },
];

const STEPS = [
  {
    n: '01',
    title: 'Import',
    body: 'Connect your repo or push strings via the CLI. Fluentix pulls in your PO/JSON source files and detects exactly what’s missing.',
  },
  {
    n: '02',
    title: 'Translate',
    body: 'AI fills the gaps using your glossary and translation memory; your team reviews and approves in one workspace.',
  },
  {
    n: '03',
    title: 'Publish',
    body: 'Approved translations export to flat JSON/PO and publish to S3 or trigger your CI — landing live in your apps automatically.',
  },
];

function LandingNav() {
  const auth = useAuth();
  return (
    <header className="sticky top-0 z-30 border-b bg-white/85 backdrop-blur" style={{ borderColor: BRAND.line }}>
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
        <a href="#top" className="flex items-center gap-2" aria-label="CoreFi Fluentix — home">
          <img src="/corefi-logo-300.png" alt="CoreFi" className="h-7 w-auto" width={300} height={109} />
          <span
            className="hidden rounded-full px-2 py-0.5 text-xs font-semibold tracking-wide sm:inline"
            style={{ backgroundColor: `${BRAND.teal}1a`, color: BRAND.tealDark }}
          >
            Fluentix
          </span>
        </a>
        <nav className="flex items-center gap-6 text-sm" aria-label="Primary">
          <a href="#features" className="hidden font-medium text-[#5c6473] hover:text-[#0e1622] md:inline">
            Features
          </a>
          <a href="#how" className="hidden font-medium text-[#5c6473] hover:text-[#0e1622] md:inline">
            How it works
          </a>
          {/* Shown at every breakpoint: the docs must be one click from here. */}
          <Link to="/docs" className="font-medium text-[#5c6473] hover:text-[#0e1622]">
            Docs
          </Link>
          {auth.isAuthenticated ? (
            <Link
              to="/workspaces"
              className="inline-flex h-9 items-center gap-1.5 rounded-md px-4 text-sm font-semibold text-white transition-colors"
              style={{ backgroundColor: BRAND.teal }}
            >
              Go to dashboard
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => void auth.signinRedirect()}
              className="inline-flex h-9 items-center rounded-md px-4 text-sm font-semibold text-white transition-colors hover:brightness-95"
              style={{ backgroundColor: BRAND.teal }}
            >
              Sign in
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  const auth = useAuth();
  return (
    <section
      id="top"
      className="relative overflow-hidden"
      style={{ background: `linear-gradient(180deg, #ffffff 0%, ${BRAND.wash} 100%)` }}
      aria-labelledby="hero-heading"
    >
      {/* Decorative accent glow, hidden from AT. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 right-[-10%] h-72 w-72 rounded-full opacity-20 blur-3xl"
        style={{ backgroundColor: BRAND.teal }}
      />
      <div className="mx-auto max-w-6xl px-5 py-20 md:py-28">
        <span
          className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium"
          style={{ borderColor: BRAND.line, color: BRAND.tealDark, backgroundColor: '#ffffff' }}
        >
          <Sparkles className="size-3.5" aria-hidden />
          AI-native translation management
        </span>
        <h1
          id="hero-heading"
          className="mt-6 max-w-3xl text-4xl font-bold leading-[1.1] tracking-tight md:text-6xl"
          style={{ color: BRAND.ink }}
        >
          Ship every language as fast as you ship code.
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed md:text-xl" style={{ color: BRAND.slate }}>
          Fluentix is the AI-native translation platform that imports your strings, translates them
          with glossary-aware AI, and publishes to your apps automatically — all wired into your CLI
          and CI.
        </p>
        <div className="mt-9 flex flex-wrap items-center gap-3">
          {auth.isAuthenticated ? (
            <Link
              to="/workspaces"
              className="inline-flex h-11 items-center gap-2 rounded-md px-6 text-base font-semibold text-white transition-colors hover:brightness-95"
              style={{ backgroundColor: BRAND.teal }}
            >
              Go to dashboard
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => void auth.signinRedirect()}
              className="inline-flex h-11 items-center gap-2 rounded-md px-6 text-base font-semibold text-white transition-colors hover:brightness-95"
              style={{ backgroundColor: BRAND.teal }}
            >
              Start translating
              <ArrowRight className="size-4" aria-hidden />
            </button>
          )}
          <a
            href="#how"
            className="inline-flex h-11 items-center rounded-md border px-6 text-base font-semibold transition-colors hover:bg-white"
            style={{ borderColor: BRAND.line, color: BRAND.ink }}
          >
            See how it works
          </a>
        </div>
        <p className="mt-5 text-sm" style={{ color: BRAND.slate }}>
          Sign in with your CoreFi account. Need access?{' '}
          <LeadButton className="font-medium underline underline-offset-4" style={{ color: BRAND.tealDark }}>
            Request an invite
          </LeadButton>
          .
        </p>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="features" className="bg-white py-20 md:py-24" aria-labelledby="features-heading">
      <div className="mx-auto max-w-6xl px-5">
        <h2 id="features-heading" className="text-3xl font-bold tracking-tight md:text-4xl" style={{ color: BRAND.ink }}>
          Everything you need to localize at speed
        </h2>
        <p className="mt-4 max-w-2xl text-lg" style={{ color: BRAND.slate }}>
          From source import to reviewed translation to live publish — one platform, model-agnostic
          across Anthropic and OpenAI, with the auditability European teams expect.
        </p>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-xl border p-6 transition-shadow hover:shadow-sm"
              style={{ borderColor: BRAND.line, backgroundColor: '#ffffff' }}
            >
              <div
                className="flex size-11 items-center justify-center rounded-lg"
                style={{ backgroundColor: `${BRAND.teal}14`, color: BRAND.tealDark }}
              >
                <Icon className="size-5" aria-hidden />
              </div>
              <h3 className="mt-4 text-lg font-semibold" style={{ color: BRAND.ink }}>
                {title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: BRAND.slate }}>
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how" className="py-20 md:py-24" style={{ backgroundColor: BRAND.wash }} aria-labelledby="how-heading">
      <div className="mx-auto max-w-6xl px-5">
        <h2 id="how-heading" className="text-3xl font-bold tracking-tight md:text-4xl" style={{ color: BRAND.ink }}>
          Import, translate, publish
        </h2>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {STEPS.map((step) => (
            <div key={step.n}>
              <div className="text-sm font-bold tracking-widest" style={{ color: BRAND.teal }}>
                {step.n}
              </div>
              <h3 className="mt-3 text-xl font-semibold" style={{ color: BRAND.ink }}>
                {step.title}
              </h3>
              <p className="mt-2 text-base leading-relaxed" style={{ color: BRAND.slate }}>
                {step.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  const auth = useAuth();
  return (
    <section className="py-20 md:py-24" style={{ backgroundColor: BRAND.deep }} aria-labelledby="cta-heading">
      <div className="mx-auto max-w-4xl px-5 text-center">
        <h2 id="cta-heading" className="text-3xl font-bold tracking-tight text-white md:text-4xl">
          Localization that keeps up with your roadmap.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-lg text-white/70">
          Stop treating translation as a separate project. Wire Fluentix into your pipeline and let
          every release ship fully localized.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {auth.isAuthenticated ? (
            <Link
              to="/workspaces"
              className="inline-flex h-11 items-center gap-2 rounded-md bg-white px-7 text-base font-semibold transition-colors hover:bg-white/90"
              style={{ color: BRAND.deep }}
            >
              Go to dashboard
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => void auth.signinRedirect()}
              className="inline-flex h-11 items-center gap-2 rounded-md bg-white px-7 text-base font-semibold transition-colors hover:bg-white/90"
              style={{ color: BRAND.deep }}
            >
              Start translating
              <ArrowRight className="size-4" aria-hidden />
            </button>
          )}
          <LeadButton className="inline-flex h-11 items-center rounded-md border border-white/25 px-7 text-base font-semibold text-white transition-colors hover:bg-white/10">
            Talk to the team
          </LeadButton>
        </div>
      </div>
    </section>
  );
}

function LandingFooter() {
  return (
    <footer className="border-t bg-white py-12" style={{ borderColor: BRAND.line }}>
      <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 px-5 md:flex-row md:items-start">
        <div className="max-w-md">
          <img src="/corefi-logo-300.png" alt="CoreFi" className="h-6 w-auto" width={300} height={109} />
          <p className="mt-3 text-sm leading-relaxed" style={{ color: BRAND.slate }}>
            Fluentix is CoreFi’s AI-native translation-management platform — glossary-aware AI
            translation, developer-first tooling, and automated publishing, built on a European
            compliance foundation.
          </p>
        </div>
        <nav className="flex items-center gap-6 text-sm" aria-label="Footer">
          <a href="#features" className="font-medium hover:underline" style={{ color: BRAND.slate }}>
            Features
          </a>
          <a href="#how" className="font-medium hover:underline" style={{ color: BRAND.slate }}>
            How it works
          </a>
          <Link to="/docs" className="font-medium hover:underline" style={{ color: BRAND.slate }}>
            Docs
          </Link>
          <LeadButton className="font-medium hover:underline" style={{ color: BRAND.slate }}>
            Request access
          </LeadButton>
        </nav>
      </div>
      <div className="mx-auto mt-8 max-w-6xl px-5 text-xs" style={{ color: BRAND.slate }}>
        © 2026 CoreFi. All rights reserved.
      </div>
    </footer>
  );
}

export function LandingPage() {
  useLeadFormEmbed();
  return (
    <div className="min-h-full bg-white" style={{ color: BRAND.ink }}>
      <LandingNav />
      <main>
        <Hero />
        <Features />
        <HowItWorks />
        <CtaBand />
      </main>
      <LandingFooter />
    </div>
  );
}
